import React, { useCallback, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/LanguageContext";
import useAdminLoad from "../../hooks/useAdminLoad";
import { REVIEW_PATHS, REQUEST_STATES, TOKEN_QUERY_PARAM } from "../contract";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * "Leave a review" on the consultation's exit screen.
 *
 * ONE READ, WITH THE BOOKING'S OWN CAPABILITY. `GET /reviews/by-booking?token=<manage_token>`
 * is resolved server-side through the tenant-scoped manage-token lookup — the room page runs
 * on the practice's origin, which is exactly where that lookup is correct. The review token
 * itself never reaches this component: it lives inside the `review_url` the server hands
 * back, and only while the request is still `pending`.
 *
 * SILENCE IS THE DEFAULT, AND IT IS NOT AN ERROR HANDLER. Three things make this component
 * render nothing, and none of them is a problem a patient can act on:
 *
 *   · the call FAILS — 404 `REVIEW_TOKEN_UNKNOWN` is the uniform answer for "this booking
 *     has no review request", which is the normal state of a no-show, of a consultation
 *     completed while the program was paused, and of every booking on a backend that
 *     predates this module. A red alert there would invent a fault out of an invitation
 *     that was never owed;
 *   · the state is `expired` — the 30 days are over and there is nothing left to offer;
 *   · there is no token at all (the practitioner's mount of the room has no `manage_token`;
 *     they are identified by their owner session, and the review is not theirs to leave).
 *
 * The load error is therefore deliberately not surfaced. That is the ONE place in this
 * module where a failed read is swallowed, and it is swallowed because the read is an
 * OFFER, not data the screen claims to be showing — the BUG-051 rule ("a failed read is not
 * an empty list") is about a screen stating a fact it does not have; this screen states
 * nothing at all.
 *
 * @param {?string} manageToken the booking's `manage_token`; absent on the practitioner's mount
 */
export default function ReviewInviteCta({ manageToken }) {
  const t = useT("public");
  const [invite, setInvite] = useState(null);

  const load = useCallback(async () => {
    if (!manageToken) return;
    const { data } = await api.get(REVIEW_PATHS.byBooking, {
      params: { [TOKEN_QUERY_PARAM]: manageToken },
    });
    // A 200 that is not a review state is not an invitation — the production proxy answers
    // 200 + index.html for an unknown /api path, and rendering a link out of that would
    // send the patient to `undefined`.
    setInvite(data && typeof data === "object" && REQUEST_STATES.includes(data.state) ? data : null);
  }, [manageToken]);

  useAdminLoad(load);

  if (invite?.state === "pending" && invite.review_url) {
    return (
      <p className="m-0">
        {/* An ABSOLUTE URL from the server, so a plain anchor and never a router link: the
            form lives on the practice's public origin, which the room page may or may not
            already be on (the practitioner's mount is under /admin). */}
        <a
          href={invite.review_url}
          data-testid={REVIEWS.roomInvite}
          className="inline-flex items-center px-4 h-10 rounded-full bg-eh-sage-deep text-white text-sm font-medium hover:opacity-90 transition"
        >
          {t("consult.room.ended.leaveReview")}
        </a>
      </p>
    );
  }

  if (invite?.state === "submitted") {
    return (
      <p className="m-0 text-sm text-eh-muted" data-testid={REVIEWS.roomInviteDone}>
        {t("consult.room.ended.reviewed")}
      </p>
    );
  }

  return null;
}
